import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December'
];

const toDateKey = (d) => {
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const classifyReminder = (r) => {
  if (r.IsCompleted) return 'done';
  const now = new Date();
  const fire = r.SnoozeUntil ? new Date(r.SnoozeUntil) : new Date(r.ReminderTime);
  if (fire < now) return 'overdue';
  const today = toDateKey(now);
  const fireKey = toDateKey(fire);
  if (fireKey === today) return 'today';
  return 'upcoming';
};

export default function ReminderCalendar({ reminders, onAddForDay }) {
  const navigate = useNavigate();
  const now = new Date();
  const [year, setYear]         = useState(now.getFullYear());
  const [month, setMonth]       = useState(now.getMonth());   // 0-indexed
  const [selectedKey, setSelectedKey] = useState(null);       // "YYYY-MM-DD"
  const [hoveredKey, setHoveredKey]   = useState(null);       // "YYYY-MM-DD"

  // Only show timed reminders on the calendar
  const timedReminders = reminders.filter(r => r.ReminderTime);

  // Group by date key
  const byDay = {};
  timedReminders.forEach(r => {
    const key = toDateKey(new Date(r.ReminderTime));
    if (!byDay[key]) byDay[key] = [];
    byDay[key].push(r);
  });

  // Calendar grid
  const firstDay = new Date(year, month, 1).getDay();   // 0=Sun
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const prevDays = new Date(year, month, 0).getDate();  // days in prev month

  const cells = [];
  // Leading cells from prev month
  for (let i = firstDay - 1; i >= 0; i--) {
    cells.push({ day: prevDays - i, key: null, outside: true });
  }
  // This month
  for (let d = 1; d <= daysInMonth; d++) {
    const pad = n => String(n).padStart(2, '0');
    const key = `${year}-${pad(month + 1)}-${pad(d)}`;
    cells.push({ day: d, key, outside: false });
  }
  // Trailing cells
  const trailing = 7 - (cells.length % 7);
  if (trailing < 7) {
    for (let d = 1; d <= trailing; d++) {
      cells.push({ day: d, key: null, outside: true });
    }
  }

  const prevMonth = () => {
    if (month === 0) { setYear(y => y - 1); setMonth(11); }
    else setMonth(m => m - 1);
    setSelectedKey(null);
  };
  const nextMonth = () => {
    if (month === 11) { setYear(y => y + 1); setMonth(0); }
    else setMonth(m => m + 1);
    setSelectedKey(null);
  };

  const todayKey = toDateKey(now);
  const selectedReminders = selectedKey ? (byDay[selectedKey] || []) : [];

  return (
    <div className="cal-wrapper">
      {/* ── Month navigation ──────────────────────────────────── */}
      <div className="cal-nav">
        <button className="cal-nav-btn" onClick={prevMonth}><i className="fas fa-chevron-left"></i></button>
        <span className="cal-month-label">{MONTH_NAMES[month]} {year}</span>
        <button className="cal-nav-btn" onClick={nextMonth}><i className="fas fa-chevron-right"></i></button>
        <button className="cal-today-btn" onClick={() => { setYear(now.getFullYear()); setMonth(now.getMonth()); setSelectedKey(todayKey); }}>
          Today
        </button>
      </div>

      {/* ── Day-of-week headers ───────────────────────────────── */}
      <div className="cal-grid">
        {DAY_LABELS.map(l => (
          <div key={l} className="cal-dow">{l}</div>
        ))}

        {/* ── Day cells ─────────────────────────────────────────── */}
        {cells.map((cell, idx) => {
          if (cell.outside) {
            return <div key={`out-${idx}`} className="cal-cell cal-cell-outside"><span className="cal-day-num">{cell.day}</span></div>;
          }

          const cellReminders = byDay[cell.key] || [];
          const isToday  = cell.key === todayKey;
          const isSelected = cell.key === selectedKey;
          const dots = buildDots(cellReminders);

          const isHovered = hoveredKey === cell.key;

          return (
            <div
              key={cell.key}
              className={`cal-cell ${isToday ? 'cal-cell-today' : ''} ${isSelected ? 'cal-cell-selected' : ''} ${cellReminders.length > 0 ? 'cal-cell-has-items' : ''}`}
              onClick={() => setSelectedKey(cell.key === selectedKey ? null : cell.key)}
              onMouseEnter={() => cellReminders.length > 0 && setHoveredKey(cell.key)}
              onMouseLeave={() => setHoveredKey(null)}
            >
              <span className="cal-day-num">{cell.day}</span>
              {dots.length > 0 && (
                <div className="cal-dots">
                  {dots.slice(0, 3).map((dot, i) => (
                    <span key={i} className={`cal-dot cal-dot-${dot}`}></span>
                  ))}
                  {dots.length > 3 && <span className="cal-dot-more">+{dots.length - 3}</span>}
                </div>
              )}
              {isHovered && cellReminders.length > 0 && (
                <div className="cal-tooltip">
                  <div className="cal-tooltip-date">
                    {new Date(cell.key + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                  </div>
                  <ul className="cal-tooltip-list">
                    {cellReminders.map(r => {
                      const type = classifyReminder(r);
                      const timeStr = new Date(r.ReminderTime).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
                      return (
                        <li key={r.ReminderID} className={`cal-tooltip-item cal-tooltip-item-${type}`}>
                          <span className={`cal-dot cal-dot-${type}`}></span>
                          <span className="cal-tooltip-title">{r.Title}</span>
                          <span className="cal-tooltip-time">{timeStr}</span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ── Selected day panel ───────────────────────────────── */}
      {selectedKey && (
        <div className="cal-day-panel">
          <div className="cal-day-panel-header">
            <span className="cal-day-panel-title">
              {new Date(selectedKey + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
            </span>
            <div className="cal-panel-header-actions">
              {onAddForDay && (
                <button className="cal-add-day-btn" onClick={() => onAddForDay(selectedKey)}>
                  <i className="fas fa-plus"></i> Add task
                </button>
              )}
              <button className="cal-panel-close" onClick={() => setSelectedKey(null)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
          </div>

          {selectedReminders.length === 0 ? (
            <p className="cal-panel-empty">No reminders on this day.</p>
          ) : (
            <ul className="cal-panel-list">
              {selectedReminders.map(r => {
                const type = classifyReminder(r);
                const timeStr = new Date(r.ReminderTime).toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
                return (
                  <li key={r.ReminderID} className={`cal-panel-item cal-panel-item-${type}`}>
                    <span className={`cal-panel-dot cal-dot-${type}`}></span>
                    <div className="cal-panel-body">
                      <div className="cal-panel-header">
                        <span className={`cal-panel-title${r.IsCompleted ? ' completed' : ''}`}>{r.Title}</span>
                        <span className="cal-panel-time"><i className="fas fa-clock"></i> {timeStr}</span>
                      </div>
                      {r.Notes && (
                        <span className="cal-panel-notes">{r.Notes}</span>
                      )}
                      <div className="cal-panel-meta">
                        {r.LinkedOrderNo && (
                          <button className="cal-panel-link" onClick={() => navigate(`/work-order-profile/${r.LinkedOrderNo}`)}>
                            <i className="fas fa-file-alt"></i> Order #{r.LinkedOrderNo}
                          </button>
                        )}
                        {r.LinkedCustomerName && (
                          <button className="cal-panel-link" onClick={() => r.LinkedCustomerID && navigate(`/customers/${r.LinkedCustomerID}`)}>
                            <i className="fas fa-user"></i> {r.LinkedCustomerName}
                          </button>
                        )}
                        {r.AssignedTo && <span className="cal-panel-assigned">→ {r.AssignedTo}</span>}
                        {r.AssignedBy && <span className="cal-panel-assigned">from {r.AssignedBy}</span>}
                      </div>
                    </div>
                    {r.IsCompleted && <i className="fas fa-check-circle cal-panel-done-icon"></i>}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {/* ── Legend ───────────────────────────────────────────── */}
      <div className="cal-legend">
        <span><span className="cal-dot cal-dot-overdue"></span> Overdue</span>
        <span><span className="cal-dot cal-dot-today"></span> Today</span>
        <span><span className="cal-dot cal-dot-upcoming"></span> Upcoming</span>
        <span><span className="cal-dot cal-dot-done"></span> Done</span>
      </div>
    </div>
  );
}

function buildDots(reminders) {
  return reminders.map(classifyReminder);
}
